import { useId } from "react";
import { minimatch } from "minimatch";
import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";
import type { ImageWidget } from "~/types/widgets";

// No Fresh era `asset()`; aqui `public/` é servido na raiz.
function resolveImageSrc(src: string) {
  if (!src) return src;
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) {
    return src;
  }
  return src.startsWith("/") ? src : `/${src}`;
}

/**
 * @titleBy label
 */
export interface CategoryNavItem {
  /**
   * @title Texto
   */
  label: string;
  /**
   * @title Link
   */
  href: string;
  /**
   * @title Ícone / thumb
   */
  image: ImageWidget;
  /**
   * @title Texto alternativo
   */
  alt?: string;
}

/**
 * @titleBy matcher
 */
export interface CategoryNavGroup {
  /**
   * @title Matcher de URL
   * @description Ex.: /beleza-e-higiene* — define em quais departamentos o bloco aparece
   */
  matcher: string;
  /**
   * @title Ativo
   * @description Desative para ocultar este grupo sem remover a configuração
   * @default true
   */
  enabled?: boolean;
  /**
   * @title Título
   * @default Navegue por Categorias
   */
  title?: string;
  /**
   * @title Categorias
   */
  items: CategoryNavItem[];
}

export interface DepartmentCategoryNavProps {
  title?: string;
  items: CategoryNavItem[];
}

function CategoryCard({ label, href, image, alt }: CategoryNavItem) {
  return (
    <a
      href={href}
      title={alt ?? label}
      className="group flex flex-col items-center shrink-0 w-20 lg:w-[120px]"
    >
      <div className="flex items-center justify-center size-20 lg:size-[120px] rounded-full bg-white border border-[#FFE2D7] overflow-hidden transition-colors duration-200 group-hover:border-[#F0863F]">
        <img
          src={resolveImageSrc(image)}
          alt={alt ?? label}
          width={48}
          height={75}
          loading="lazy"
          decoding="async"
          className="object-contain w-[38%] h-[52%] lg:w-[40%] lg:h-[51%]"
        />
      </div>
      <span className="mt-1 text-sm font-medium text-[#333] text-center leading-7 line-clamp-2">
        {label}
      </span>
    </a>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M15.75 19.5L8.25 12L15.75 4.5"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8.25 4.5L15.75 12L8.25 19.5"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Carrossel de thumbs "Navegue por Categorias" para páginas de departamento.
 * Desktop: setas. Mobile: cards menores, scroll lateral sem setas.
 */
export default function DepartmentCategoryNav({
  title = "Navegue por Categorias",
  items,
}: DepartmentCategoryNavProps) {
  const id = useId();

  if (!items?.length) return null;

  // 7 cards (120px) + 6 gaps (57px) = 1182px
  const desktopTrackMaxWidth = "calc(7 * 120px + 6 * 57px)";

  return (
    <div id={id} className="flex flex-col gap-4 w-full pb-2 lg:pb-0">
      <h3 className="text-lg font-extrabold text-[#333] leading-7 lg:h-[47px] lg:flex lg:items-center">
        {title}
      </h3>

      <div className="relative flex items-center justify-center w-full">
        <Slider.PrevButton
          type="button"
          className="hidden lg:flex absolute left-0 z-10 items-center justify-center size-12 rounded-full bg-[rgba(225,223,219,0.8)] border-none cursor-pointer disabled:opacity-40 transition-opacity"
        >
          <ChevronLeftIcon />
        </Slider.PrevButton>

        <Slider
          className="carousel carousel-center lg:carousel-start gap-2.5 lg:gap-[57px] w-full min-w-0 overflow-x-auto scrollbar-none lg:mx-14"
          style={{ maxWidth: desktopTrackMaxWidth }}
        >
          {items.map((item, index) => (
            <Slider.Item key={item.href} index={index} className="carousel-item">
              <CategoryCard {...item} />
            </Slider.Item>
          ))}
        </Slider>

        <Slider.NextButton
          type="button"
          className="hidden lg:flex absolute right-0 z-10 items-center justify-center size-12 rounded-full bg-[rgba(225,223,219,0.8)] border-none cursor-pointer disabled:opacity-40 transition-opacity"
        >
          <ChevronRightIcon />
        </Slider.NextButton>
      </div>

      <SliderJS rootId={id} />
    </div>
  );
}

export function matchCategoryNav(
  groups: CategoryNavGroup[] | undefined,
  url: string,
): CategoryNavGroup | undefined {
  if (!groups?.length) return undefined;

  // `url` chega relativo em navegação SPA — `new URL` sem base crasha a section.
  const pathname = new URL(url, "http://localhost").pathname.toLowerCase();

  // Grupo com `enabled: false` não casa (miess-01#328): o CMS desliga o bloco de
  // um departamento sem apagar a configuração. Ausente conta como ativo.
  return groups.find(
    ({ matcher, enabled = true }) =>
      enabled && matcher && minimatch(pathname, matcher.toLowerCase()),
  );
}
